import {
  checkAmount,
  type AmountBounds,
  type AmountCheck,
} from "@/modules/dashboard/crypto-swaps/utils/cryptoSwapAmounts";
import { formatAmount } from "@/modules/dashboard/crypto-swaps/utils/cryptoSwapFormat";
import { isDecimalString } from "@/shared/utils/decimal";

import type { CardCopTopUpStatus, CardCopTopUpTerms } from "../types/cardCopTopUp.types";

const SCALE = 8;
const FACTOR = 10n ** BigInt(SCALE);
// The card provider states a deposit at six decimals, truncated; the desk delivers USDT at two.
const CREDIT_STEP = 10n ** BigInt(SCALE - 6);
const USDT_STEP = 10n ** BigInt(SCALE - 2);

function toScaled(value: string): bigint {
  const [whole, fraction = ""] = value.trim().split(".");
  return BigInt(whole || "0") * FACTOR + BigInt(fraction.slice(0, SCALE).padEnd(SCALE, "0"));
}

function fromScaled(scaled: bigint): string {
  return `${scaled / FACTOR}.${(scaled % FACTOR).toString().padStart(SCALE, "0")}`;
}

function isUsable(value: string | null | undefined): value is string {
  return !!value && isDecimalString(value.trim()) && !value.trim().startsWith("-");
}

function creditedScaled(gross: bigint, percent: bigint): bigint {
  const divisor = 100n * FACTOR;
  const fee = (gross * percent + divisor / 2n) / divisor;
  const net = gross - fee;
  return net - (net % CREDIT_STEP);
}

/** What a deposit of `gross` USDT credits to the card at `percent` deposit fee. Mirrors the backend. */
export function creditedFor(gross: string, percent: string): string | null {
  if (!isUsable(gross) || !isUsable(percent)) return null;
  return fromScaled(creditedScaled(toScaled(gross), toScaled(percent)));
}

/** The smallest two-decimal USDT deposit that credits at least `credited`; null when no deposit can. */
export function grossFor(credited: string, percent: string): string | null {
  if (!isUsable(credited) || !isUsable(percent)) return null;
  const target = toScaled(credited);
  const fee = toScaled(percent);
  const kept = 100n * FACTOR - fee;
  if (target <= 0n || kept <= 0n) return null;
  const exact = (target * 100n * FACTOR + kept - 1n) / kept;
  let gross = ((exact + USDT_STEP - 1n) / USDT_STEP) * USDT_STEP;
  while (creditedScaled(gross, fee) < target) gross += USDT_STEP;
  return fromScaled(gross);
}

/** Drops digits past `decimals` without rounding: an estimate of what arrives must never read high. */
/**
 * A rate as "1 USD = 3.311,25 COP", to the cent and rounded. Null without a rate, so a row for it is simply
 * not drawn.
 */
export function formatCopRate(
  value: string | null | undefined,
  unit: "USD" | "USDT",
  locale: string,
  estimated = false,
): string | null {
  const trimmed = value?.trim();
  if (!trimmed || !isDecimalString(trimmed)) return null;
  const rate = formatAmount(trimmed, locale, { min: 2, max: 2 });
  return `${estimated ? "≈ " : ""}1 ${unit} = ${rate} COP`;
}

export function truncateDecimals(value: string, decimals: number): string {
  const [whole, fraction = ""] = value.trim().split(".");
  return decimals > 0 ? `${whole}.${fraction.slice(0, decimals).padEnd(decimals, "0")}` : whole;
}

export type CopTopUpAnchor = "send" | "credit";

export type CopTopUpIssue =
  | { kind: "invalid" }
  | { kind: "precision" }
  | { kind: "below_min"; bound: string; currency: "COP" | "USD" }
  | { kind: "above_max"; bound: string; currency: "COP" | "USD" }
  | { kind: "balance" };

export interface CopTopUpRequestInput {
  anchor: CopTopUpAnchor;
  amount: string;
  percent: string;
  /** COP bounds of one purchase. */
  sendBounds: AmountBounds | null;
  /** USDT bounds of one purchase. */
  receiveBounds: AmountBounds | null;
}

export type CopTopUpRequest =
  | { quote: { send_amount: string } | { receive_amount: string }; issue: null }
  | { quote: null; issue: CopTopUpIssue | null };

const NOTHING: CopTopUpRequest = { quote: null, issue: null };

// A typed USD credit is priced as the USDT that credits it at the card's fee; the USDT bounds are told back in USD.
export function copTopUpRequest(input: CopTopUpRequestInput): CopTopUpRequest {
  const amount = input.amount.trim();
  if (!amount) return NOTHING;

  if (input.anchor === "send") {
    const check = checkAmount(amount, { decimals: 0, bounds: input.sendBounds });
    if (check === "ok") return { quote: { send_amount: amount }, issue: null };
    return { quote: null, issue: issueOf(check, input.sendBounds, "COP", (bound) => bound) };
  }

  const check = checkAmount(amount, { decimals: 2 });
  if (check !== "ok") return { quote: null, issue: issueOf(check, null, "USD", (bound) => bound) };
  const gross = grossFor(amount, input.percent);
  if (!gross) return { quote: null, issue: { kind: "invalid" } };
  const usdtCheck = checkAmount(gross, { decimals: 8, bounds: input.receiveBounds });
  if (usdtCheck === "ok") return { quote: { receive_amount: truncateDecimals(gross, 2) }, issue: null };
  const inUsd = (bound: string) => truncateDecimals(creditedFor(bound, input.percent) ?? bound, 2);
  return { quote: null, issue: issueOf(usdtCheck, input.receiveBounds, "USD", inUsd) };
}

function issueOf(
  check: AmountCheck,
  bounds: AmountBounds | null,
  currency: "COP" | "USD",
  express: (bound: string) => string,
): CopTopUpIssue | null {
  switch (check) {
    case "empty":
      return null;
    case "precision":
      return { kind: "precision" };
    case "below_min":
      return bounds?.min ? { kind: "below_min", bound: express(bounds.min), currency } : { kind: "invalid" };
    case "above_max":
      return bounds?.max ? { kind: "above_max", bound: express(bounds.max), currency } : { kind: "invalid" };
    default:
      return { kind: "invalid" };
  }
}

const LIVE_STATUSES: readonly string[] = ["PROCESSING", "DELIVERED"];

export function isCopTopUpLive(status: CardCopTopUpStatus): boolean {
  return LIVE_STATUSES.includes(status);
}

export const COP_TOP_UPS_NOT_ENABLED = "card_cop_top_ups_not_enabled";

// Off for the organization means no option at all; any other refusal shows it disabled with its reason.
export function isCopTopUpOffered(terms: CardCopTopUpTerms | null): boolean {
  return !!terms && terms.unavailable_reason !== COP_TOP_UPS_NOT_ENABLED;
}

export function copTopUpUnavailableKey(
  reason: string | null,
):
  | "cop_top_up_unavailable_permission"
  | "cop_top_up_unavailable_card"
  | "cop_top_up_unavailable_card_inactive"
  | "cop_top_up_unavailable_crypto_off"
  | "cop_top_up_unavailable_breb_key"
  | "cop_top_up_unavailable_verification"
  | "cop_top_up_unavailable_fee"
  | "cop_top_up_unavailable_maintenance"
  | "cop_top_up_unavailable_generic" {
  switch (reason) {
    case "missing_permission":
      return "cop_top_up_unavailable_permission";
    case "card_not_eligible":
    case "card_asset_unsupported":
      return "cop_top_up_unavailable_card";
    case "card_not_active":
      return "cop_top_up_unavailable_card_inactive";
    case "otc_not_enabled":
      return "cop_top_up_unavailable_crypto_off";
    case "breb_key_required":
      return "cop_top_up_unavailable_breb_key";
    case "verification_required":
      return "cop_top_up_unavailable_verification";
    case "deposit_fee_unavailable":
    case "deposit_address_unavailable":
      return "cop_top_up_unavailable_fee";
    case "rail_disabled":
      return "cop_top_up_unavailable_maintenance";
    default:
      return "cop_top_up_unavailable_generic";
  }
}
